//import components
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  SxProps,
  TextField
} from '@mui/material';
import VirtualizedAutocomplete from '../Autocomplete/VirtualizedAutocomplete';

// import constants
import { GRAY_LIGHT_TERTIARY, WHITE } from 'constants/colors';

// import hooks
import { Control, Controller } from 'react-hook-form';
import { Icon } from '@sdb/planning-ui';

interface Props {
  allowItemRemoval?: boolean;
  control: Control<any>;
  fullWidth?: boolean;
  disabled?: boolean;
  label: string;
  idProperty: string;
  labelProperty: string;
  listProps?: SxProps;
  name: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveElement?: (id: string | number) => void;
  options: any[];
  size?: 'small' | 'medium';
  variant?: 'standard' | 'outlined';
}

const ControlledCheckboxAutoComplete = (props: Props) => {
  const {
    control,
    disabled,
    fullWidth,
    idProperty,
    label,
    labelProperty,
    listProps,
    name,
    allowItemRemoval,
    onRemoveElement,
    options,
    size = 'small',
    variant = 'outlined',
    onChange
  } = props;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <>
          <VirtualizedAutocomplete
            disabled={disabled}
            disableCloseOnSelect
            multiple
            size={size}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                error={!!fieldState?.error?.type}
                helperText={fieldState?.error?.message}
                fullWidth={fullWidth}
                variant={variant}
              />
            )}
            renderOption={(_, option) => {
              const handleToggle = () => {
                const event = field.value?.includes(
                  option[idProperty] || option
                )
                  ? field.value?.filter(
                      (id) => id !== (option[idProperty] || option)
                    )
                  : [...(field.value || []), option[idProperty]];

                field.onChange(event);

                onChange?.(event);
              };

              return (
                <FormControlLabel
                  key={option[idProperty] || option}
                  label={option[labelProperty] || option}
                  checked={
                    !!(field.value || [])?.find(
                      (id) => id === (idProperty ? option[idProperty] : option)
                    )
                  }
                  control={<Checkbox sx={{ ml: 2 }} />}
                  onChange={handleToggle}
                  onClick={(e) => {
                    e.preventDefault();
                    handleToggle();
                  }}
                />
              );
            }}
            onChange={field.onChange}
            options={options}
            getOptionLabel={(option) => option[labelProperty] || option}
            optionLabelProp={labelProperty}
            isOptionEqualToValue={(a, b) =>
              idProperty ? a[idProperty] === b[idProperty] : a === b
            }
          />

          {(field.value || []).length > 0 && (
            <List
              sx={{
                backgroundColor: GRAY_LIGHT_TERTIARY,
                px: 0,
                borderRadius: 1,
                py: 0,
                mt: 5,
                mb: 0,
                ...listProps
              }}
            >
              {(field.value || []).map((id: number, i: number) => (
                <ListItem
                  key={i}
                  sx={{
                    px: 4,
                    borderTop: i === 0 ? 'none' : `1px solid ${WHITE}`,
                    borderBottom:
                      i === field.value.length - 1
                        ? 'none'
                        : `1px solid ${WHITE}`
                  }}
                  secondaryAction={
                    allowItemRemoval ? (
                      <IconButton
                        onClick={() => onRemoveElement?.(id)}
                        size="large"
                      >
                        <Icon
                          type="xmark"
                          fontSize="small"
                          sx={{ fontSize: 13 }}
                        />
                      </IconButton>
                    ) : null
                  }
                >
                  <ListItemText
                    primary={
                      options.find(
                        (option) => (option[idProperty] || option) === id
                      )?.[labelProperty]
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </>
      )}
    />
  );
};

export default ControlledCheckboxAutoComplete;
